/**
 * useBackClose — 안드로이드 뒤로가기(BackGuard)로 이 팝업/오버레이/드롭다운을 닫는다.
 *
 * #449 재설계(pushState 방식): 예전엔 BackGuard 가 Navigation API 의 preventDefault() 로
 * 뒤로가기를 "취소"해서 앱 종료를 막았는데, 스펙상 preventDefault() 는 user-activation 을
 * 소비하며 "중간 사용자 상호작용 없이 연속 두 번의 뒤로가기"는 취소할 수 없다(브라우저의
 * back-trapping 방지 정책). → 미니팝업을 두 번째로 열고 뒤로가기하면 취소 불가(cancelable=false)
 * 로 앱이 종료되던 원인.
 *
 * 그래서 preventDefault 에 의존하지 않고, 팝업이 열릴 때 **자기 몫의 history entry 를 하나
 * push** 한다. 뒤로가기는 그 entry 를 자연스럽게 소비하며 navigate 이벤트를 발생시키고,
 * BackGuard 는 그때 최상위 모달만 닫는다(preventDefault 안 함 → activation 예산 소모 0 →
 * 연속 뒤로가기 무제한). UI(버튼/오버레이/Esc/선택)로 닫힐 땐 push 했던 entry 를 직접 회수한다.
 *
 * @param onClose  닫기 함수(항상 최신 것을 ref 로 참조)
 * @param active   이 팝업이 "열려 있는" 동안만 true (드롭다운처럼 컴포넌트는 상주하고 열림
 *                 상태만 토글되는 경우 사용). 조건부 렌더링되는 팝업은 기본값 true 로 둔다.
 */
import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  registerModalClose,
  suppressNextPopstate,
  nextGuardHash,
} from "../utils/modalStack";
import { isNativeApp } from "../utils/nativeApp";

export function useBackClose(onClose: () => void, active: boolean = true) {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const navigate = useNavigate();
  const location = useLocation();
  const locationRef = useRef(location);
  locationRef.current = location;

  useEffect(() => {
    if (!active) return;
    // 네이티브 앱(Capacitor)에선 history 를 안 건드리고 modalStack 만 사용(네이티브 백버튼이
    // closeTopModal 로 닫음). 웹에서만 history entry 방식 사용.
    const isAndroid = /Android/i.test(navigator.userAgent || "") && !isNativeApp();
    // 고유 해시(#_gN)로 "우리 entry"를 식별 → 뒤로가기가 소비했는지 판정(중첩 모달 안전).
    let ourHash = "";
    if (isAndroid) {
      ourHash = nextGuardHash();
      navigate(
        locationRef.current.pathname + locationRef.current.search + ourHash,
        { state: { __trippang_modal_open__: true }, replace: false },
      );
    }
    const unregister = registerModalClose(() => onCloseRef.current());
    return () => {
      unregister();
      if (!isAndroid) return;
      // 우리 고유 해시가 아직 현재 = 뒤로가기가 아닌 UI 로 닫힘 → push 한 entry 를 직접 회수.
      // (뒤로가기로 닫힌 경우엔 그 entry 가 이미 소비돼 현재 해시가 달라져 있으므로 건너뜀 →
      //  이중 소비로 인한 오종료 방지)
      if (locationRef.current.hash === ourHash) {
        suppressNextPopstate();
        try {
          navigate(-1);
        } catch {
          /* */
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);
}
